import { useState } from "react";
import { AdsAssistantAggregationPostRow } from "@/components/ads-assistant/AdsAssistantAggregationPostRow";
import type { AdsPostActionsGenerateOpen } from "@/components/ads-assistant/AdsAssistantAggregationPostActions";
import { AdsAssistantKbSnippetDialog } from "@/components/ads-assistant/AdsAssistantKbSnippetDialog";
import { AdsAssistantPostMediaLightbox } from "@/components/ads-assistant/AdsAssistantPostMediaLightbox";
import type { AdsAssistantPostMediaItem, AdsAssistantSocialPostRow, AdsAssistantTemplateRow } from "@/lib/adsAssistantClient";

export type AdsGenerateOpen = AdsPostActionsGenerateOpen;

export function AdsAssistantAggregationPostsList(props: {
  posts: AdsAssistantSocialPostRow[];
  templates: AdsAssistantTemplateRow[];
  /** App location id → display name for index cards. */
  locationNameById?: Record<string, string>;
  /** Marketing / trainer / ads guidance editor — show green portable toggle. */
  canQuality?: boolean;
  /** Same gate as manual ingest — show delete post control. */
  canIngestPosts?: boolean;
  canReassignAttribution?: boolean;
  onReassignAttribution?: (post: AdsAssistantSocialPostRow) => void;
  onCelebration?: (message: string) => void;
  onReload?: () => void;
  onRequestGenerate?: (p: AdsGenerateOpen) => void;
  /** When set, each card can open a KB snippet dialog (internal lane). */
  scopedOrgId?: string | null;
}) {
  const {
    posts,
    templates,
    locationNameById,
    canQuality,
    canIngestPosts,
    canReassignAttribution,
    onReassignAttribution,
    onCelebration,
    onReload,
    onRequestGenerate,
    scopedOrgId,
  } = props;
  const [busyId, setBusyId] = useState<string | null>(null);
  const [busyGreenId, setBusyGreenId] = useState<string | null>(null);
  const [expandedPostIds, setExpandedPostIds] = useState<Set<string>>(new Set());
  const [kbPost, setKbPost] = useState<AdsAssistantSocialPostRow | null>(null);
  const [mediaLightbox, setMediaLightbox] = useState<{
    items: AdsAssistantPostMediaItem[];
    index: number;
  } | null>(null);

  function toggleBodyExpand(postId: string) {
    setExpandedPostIds((prev) => {
      const next = new Set(prev);
      if (next.has(postId)) next.delete(postId);
      else next.add(postId);
      return next;
    });
  }

  if (!posts.length) return null;

  return (
    <>
      <ul className="mt-3 space-y-2" aria-label="Recent indexed posts">
        {posts.map((p) => (
          <AdsAssistantAggregationPostRow
            key={p.id}
            post={p}
            templates={templates}
            locationNameById={locationNameById}
            expanded={expandedPostIds.has(p.id)}
            onToggleBodyExpand={toggleBodyExpand}
            onOpenLightbox={(items, initialIndex) => setMediaLightbox({ items, index: initialIndex })}
            busyId={busyId}
            busyGreenId={busyGreenId}
            setBusyId={setBusyId}
            setBusyGreenId={setBusyGreenId}
            setKbPost={setKbPost}
            scopedOrgId={scopedOrgId}
            canQuality={canQuality}
            canIngestPosts={canIngestPosts}
            canReassignAttribution={canReassignAttribution}
            onReassignAttribution={onReassignAttribution}
            onCelebration={onCelebration}
            onReload={onReload}
            onRequestGenerate={onRequestGenerate}
          />
        ))}
      </ul>
      <AdsAssistantKbSnippetDialog scopedOrgId={scopedOrgId ?? null} post={kbPost} onClose={() => setKbPost(null)} />
      <AdsAssistantPostMediaLightbox
        open={!!mediaLightbox}
        items={mediaLightbox?.items ?? []}
        index={mediaLightbox?.index ?? 0}
        onClose={() => setMediaLightbox(null)}
        onIndexChange={(i) => setMediaLightbox((s) => (s ? { ...s, index: i } : null))}
      />
    </>
  );
}
